import type {
  CallRule, OperationDoc, ProfileQueue, ProfileSegment, ProfileWorkProfile, RuleCondition, ServiceGroup,
} from './types'

// Pure edits of an operation profile's document. Every helper returns a new
// document and leaves its input alone.

/** Today's blank() timers for a new work profile (admin/Profiles.tsx). */
export const NEW_WORK_PROFILE_TIMERS = { wrapUpSec: 0, noShowSec: 90, recallIntervalSec: 20, repeatCallSec: 0 } as const

const NEW_QUEUE = { firstNumber: 1, lastNumber: 999, serviceLevelSec: 600, type: 'normal' } as const

function randomBytes(n: number): Uint8Array {
  const bytes = new Uint8Array(n)
  const source = globalThis.crypto
  if (source && typeof source.getRandomValues === 'function') source.getRandomValues(bytes)
  else for (let i = 0; i < n; i++) bytes[i] = Math.floor(Math.random() * 256)
  return bytes
}

/** RFC 9562 version 7: 48 bits of Unix milliseconds, then random bits. */
export function uuidv7(now: number = Date.now(), random: (n: number) => Uint8Array = randomBytes): string {
  const b = new Uint8Array(16)
  b.set(random(10).subarray(0, 10), 6)
  let t = Math.max(0, Math.floor(now))
  for (let i = 5; i >= 0; i--) {
    b[i] = t % 256
    t = Math.floor(t / 256)
  }
  b[6] = 0x70 | (b[6]! & 0x0f)
  b[8] = 0x80 | (b[8]! & 0x3f)
  const hex = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

// ---- normalise (Go's nil slices arrive as null) ----

type Raw = Record<string, unknown>
const obj = (x: unknown): Raw => (x && typeof x === 'object' && !Array.isArray(x) ? (x as Raw) : {})
const list = (x: unknown): unknown[] => (Array.isArray(x) ? x : [])
const strs = (x: unknown): string[] => list(x).filter((s): s is string => typeof s === 'string')
const str = (x: unknown): string => (typeof x === 'string' ? x : '')
const num = (x: unknown): number => (typeof x === 'number' && Number.isFinite(x) ? x : 0)

/** Copies the present keys of `from` onto `to`, dropping null and undefined (optional = absent). */
function keep<T extends object>(to: T, from: Raw, keys: readonly string[], fix: (v: unknown) => unknown = (v) => v): T {
  for (const k of keys) if (from[k] !== null && from[k] !== undefined) (to as Raw)[k] = fix(from[k])
  return to
}

function normSegment(raw: unknown): ProfileSegment {
  const s = obj(raw)
  const seg: ProfileSegment = { id: str(s.id), name: str(s.name), queueId: str(s.queueId) }
  keep(seg, s, ['serviceIds', 'priorities', 'languages', 'sources'], strs)
  keep(seg, s, ['appointment'], str)
  keep(seg, s, ['lateOverMin'], num)
  return seg
}

function normGroup(raw: unknown): ServiceGroup {
  const g = obj(raw)
  return {
    id: str(g.id), name: str(g.name), serviceIds: strs(g.serviceIds),
    defaultQueueId: str(g.defaultQueueId), segments: list(g.segments).map(normSegment),
  }
}

function normQueue(raw: unknown): ProfileQueue {
  const q = obj(raw)
  const name: Record<string, string> = {}
  for (const [k, v] of Object.entries(obj(q.name))) if (typeof v === 'string') name[k] = v
  return {
    id: str(q.id), code: str(q.code), name, letter: str(q.letter),
    firstNumber: num(q.firstNumber), lastNumber: num(q.lastNumber), serviceLevelSec: num(q.serviceLevelSec),
    type: q.type === 'appointment' ? 'appointment' : 'normal', sortOrder: num(q.sortOrder),
  }
}

function normCondition(raw: unknown): RuleCondition {
  const c = obj(raw)
  const cond = { kind: str(c.kind), n: num(c.n), from: num(c.from), until: num(c.until) } as RuleCondition
  return keep(cond, c, ['queueId', 'op', 'scope'], str)
}

function normRule(raw: unknown): CallRule {
  const r = obj(raw)
  const rule = {
    id: str(r.id), queueIds: strs(r.queueIds), order: str(r.order), primaryResource: str(r.primaryResource) || 'ignore',
  } as CallRule
  keep(rule, r, ['pools'], strs)
  keep(rule, r, ['shares'], (v) => {
    const shares: Record<string, number> = {}
    for (const [k, n] of Object.entries(obj(v))) shares[k] = num(n)
    return shares
  })
  keep(rule, r, ['conditions'], (v) => list(v).map(normCondition))
  return rule
}

function normWorkProfile(raw: unknown): ProfileWorkProfile {
  const w = obj(raw)
  const wp: ProfileWorkProfile = {
    id: str(w.id), name: str(w.name), queueIds: strs(w.queueIds), rules: list(w.rules).map(normRule),
    wrapUpSec: num(w.wrapUpSec), noShowSec: num(w.noShowSec),
    recallIntervalSec: num(w.recallIntervalSec), repeatCallSec: num(w.repeatCallSec),
  }
  keep(wp, w, ['pools'], strs)
  keep(wp, w, ['appointmentEarlySec'], num)
  return wp
}

/** A document safe to draw: every list present, every optional null dropped. */
export function normaliseDoc(raw: unknown): OperationDoc {
  const d = obj(raw)
  return {
    groups: list(d.groups).map(normGroup),
    queues: list(d.queues).map(normQueue),
    workProfiles: list(d.workProfiles).map(normWorkProfile),
  }
}

function canonical(x: unknown): unknown {
  if (Array.isArray(x)) return x.map(canonical)
  if (x && typeof x === 'object') {
    const out: Raw = {}
    for (const k of Object.keys(x).sort()) out[k] = canonical((x as Raw)[k])
    return out
  }
  return x
}

/** Same content, whatever the key order (the dirty test). */
export function sameDoc(a: OperationDoc, b: OperationDoc): boolean {
  return JSON.stringify(canonical(a)) === JSON.stringify(canonical(b))
}

// ---- add ----

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'

/** The first single letter A–Z no queue uses, else 'A'. */
export function freeLetter(doc: OperationDoc): string {
  const used = new Set(doc.queues.map((q) => q.letter.toUpperCase()))
  return Array.from(LETTERS).find((l) => !used.has(l)) ?? 'A'
}

export function addQueue(doc: OperationDoc, name: Record<string, string>, code: string): [OperationDoc, string] {
  const id = uuidv7()
  const sortOrder = doc.queues.reduce((m, q) => Math.max(m, q.sortOrder), 0) + 1
  const q: ProfileQueue = { id, code, name: { ...name }, letter: freeLetter(doc), ...NEW_QUEUE, sortOrder }
  return [{ ...doc, queues: [...doc.queues, q] }, id]
}

/** A group arrives with its own default queue (Part 1 §4.1). */
export function addGroup(doc: OperationDoc, groupName: string, queueName: Record<string, string>, queueCode: string): [OperationDoc, string] {
  const [withQueue, queueId] = addQueue(doc, queueName, queueCode)
  const id = uuidv7()
  const g: ServiceGroup = { id, name: groupName, serviceIds: [], defaultQueueId: queueId, segments: [] }
  return [{ ...withQueue, groups: [...withQueue.groups, g] }, id]
}

/** A new segment sends to its group's default queue until the admin picks another. */
export function addSegment(doc: OperationDoc, groupId: string, name: string): [OperationDoc, string] {
  const id = uuidv7()
  const groups = doc.groups.map((g) =>
    g.id === groupId ? { ...g, segments: [...g.segments, { id, name, queueId: g.defaultQueueId }] } : g)
  return [{ ...doc, groups }, id]
}

/** Finally: calls from everything the profile serves, so its own lists stay empty.
 *  A customer booked with a named colleague is left to them (`both`). */
export function finallyRule(): CallRule {
  return { id: uuidv7(), queueIds: [], order: 'max_waiting', primaryResource: 'both' }
}

/** As the server's profile.NewWorkProfile: the desk pool, so a held customer comes back here. */
export function addWorkProfile(doc: OperationDoc, name: string): [OperationDoc, string] {
  const id = uuidv7()
  const wp: ProfileWorkProfile = { id, name, queueIds: [], pools: ['desk'], rules: [finallyRule()], ...NEW_WORK_PROFILE_TIMERS }
  return [{ ...doc, workProfiles: [...doc.workProfiles, wp] }, id]
}

// ---- put (replace by id; an unknown id changes nothing) ----

const put = <T extends { id: string }>(items: readonly T[], next: T): T[] => items.map((x) => (x.id === next.id ? next : x))

export function putGroup(doc: OperationDoc, g: ServiceGroup): OperationDoc {
  return { ...doc, groups: put(doc.groups, g) }
}

export function putQueue(doc: OperationDoc, q: ProfileQueue): OperationDoc {
  return { ...doc, queues: put(doc.queues, q) }
}

export function putWorkProfile(doc: OperationDoc, w: ProfileWorkProfile): OperationDoc {
  return { ...doc, workProfiles: put(doc.workProfiles, w) }
}

// ---- remove ----

/** Drops the group; its default queue stays, unrouted, for the admin to keep or remove. */
export function removeGroup(doc: OperationDoc, groupId: string): OperationDoc {
  return { ...doc, groups: doc.groups.filter((g) => g.id !== groupId) }
}

export function removeWorkProfile(doc: OperationDoc, id: string): OperationDoc {
  return { ...doc, workProfiles: doc.workProfiles.filter((w) => w.id !== id) }
}

export type QueueUse = { kind: 'default' | 'segment'; groupId: string; segmentId?: string }

/** The routing that sends tickets to the queue; while any is left the queue cannot go. */
export function queueUses(doc: OperationDoc, queueId: string): QueueUse[] {
  const uses: QueueUse[] = []
  for (const g of doc.groups) {
    if (g.defaultQueueId === queueId) uses.push({ kind: 'default', groupId: g.id })
    for (const s of g.segments) if (s.queueId === queueId) uses.push({ kind: 'segment', groupId: g.id, segmentId: s.id })
  }
  return uses
}

// Same rule as profilesRules.setProfileQueues; Task 10 may fold the two together.
function withoutQueue(wp: ProfileWorkProfile, queueId: string): ProfileWorkProfile {
  const rules: CallRule[] = []
  wp.rules.forEach((r, i) => {
    const rule: CallRule = { ...r, queueIds: r.queueIds.filter((q) => q !== queueId) }
    if (r.conditions) rule.conditions = r.conditions.filter((c) => c.queueId !== queueId)
    if (r.shares) rule.shares = Object.fromEntries(Object.entries(r.shares).filter(([q]) => q !== queueId))
    const emptied = r.queueIds.length > 0 && rule.queueIds.length === 0 && !(rule.pools?.length)
    if (i === 0 || !emptied) rules.push(rule)
  })
  return { ...wp, queueIds: wp.queueIds.filter((q) => q !== queueId), rules }
}

/** Removes the queue and every work-profile reference to it; refused with the routing uses while any remain. */
export function removeQueue(doc: OperationDoc, queueId: string): OperationDoc | QueueUse[] {
  const uses = queueUses(doc, queueId)
  if (uses.length > 0) return uses
  return {
    ...doc,
    queues: doc.queues.filter((q) => q.id !== queueId),
    workProfiles: doc.workProfiles.map((w) => withoutQueue(w, queueId)),
  }
}

// ---- misc ----

export function moveItem<T>(list: readonly T[], from: number, to: number): T[] {
  const out = [...list]
  if (from < 0 || from >= out.length || to < 0 || to >= out.length || from === to) return out
  const [item] = out.splice(from, 1)
  out.splice(to, 0, item as T)
  return out
}

/** The services the group may take: all, less those another group of the profile holds. */
export function assignableServiceIds(doc: OperationDoc, groupId: string, all: readonly string[]): string[] {
  const taken = new Set(doc.groups.filter((g) => g.id !== groupId).flatMap((g) => g.serviceIds))
  return all.filter((id) => !taken.has(id))
}
